import moment from 'moment';
import { Button, Col, Form, Input, Radio, Row, Select, Tooltip } from 'antd';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { InfoCircleOutlined, CloudSyncOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import selectorUser from '@redux/user/selectors';
import {
  CustomNotification,
  StyledModal,
  TypePicker,
  WrapperSelect,
} from 'common/components';
import {
  ADVANCED_FILTER_MODE,
  DELIVERY_METHOD,
  REPORT_EXPORT_TYPE_ADVANCED,
  REPORT_EXPORT_TYPE_FLAT,
} from '../utils/constants';
import {
  getKeyFactPanels,
  useGetDownloadScheduleDetail,
  useMutationDownloadSChedule,
  useMutationFTPConfig,
} from '../hook/useScheduleDownload';
import ReportingDownloadScheduleForm from './DownloadSchedule/ReportingDownloadScheduleForm';
import ReportingScheduleFooter from './DownloadSchedule/ReportingScheduleFooter';
import { formatRangeDateTime } from 'utils/formatDate';
import { validateMultipleEmails } from 'utils';
export const DEFAULT_SETTING = {
  runsPerWeek: 0,
  intervalType: 'Daily',
  easterOrUserLocal: 'America/Belize',
  deliveryMethod: 'email',
};
const ReportingScheduleModal = (props) => {
  const [form] = Form.useForm();
  const [statusFTP, setStatusFTP] = useState({
    isValidated: false,
    isSuccess: true,
    message: '',
  });
  const queryClient = useQueryClient();
  const { openSchedule, setOpenSchedule, modeData, reportingId } = props;
  const userInfo = useSelector(selectorUser.makeSelectUserInfo());
  const { downloadScheduleDetail } = useGetDownloadScheduleDetail({
    id: reportingId,
    enabled: !!reportingId && reportingId !== 'create',
  });
  const { deleteSchedule, configSchedule } = useMutationDownloadSChedule();
  const { validateFTP } = useMutationFTPConfig();
  const deliveryMethod = Form.useWatch('deliveryMethod', form);
  const onSchedule = async () => {
    try {
      const formValues = await form.validateFields();
      if (!formValues) return;
      const test = formatRangeDateTime(formValues?.runAt);
      const newDate = new Date(
        test.getTime() - test.getTimezoneOffset() * 60000
      );
      const params = {
        ...formValues,
        reportId: +reportingId,
        runAt: newDate.toISOString(),
        userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
      configSchedule.mutate(params, {
        onSuccess: (result) => {
          if (result?.isSuccess) {
            CustomNotification.success(
              'Reporting download schedule configured successfully'
            );
            queryClient.invalidateQueries({
              queryKey: getKeyFactPanels(reportingId),
            });
            closeModal();
          } else {
            CustomNotification.error(result?.message);
          }
        },
      });
    } catch (error) {
      console.log('error: ', error);
    }
  };
  const onFormChange = (changedValues, allValue) => {
    let updatedValues = {};
    if (changedValues.specificScheduleOrdinalNumeral) {
      updatedValues.specificScheduleOrdinalNumeral = [
        changedValues?.specificScheduleOrdinalNumeral?.[
          changedValues?.specificScheduleOrdinalNumeral.length - 1
        ],
      ].filter(Boolean);
      updatedValues.specificScheduleDaysOfWeek = [];
    }
    if (changedValues.specificScheduleDaysOfWeek) {
      if (allValue.specificScheduleOrdinalNumeral.length)
        updatedValues.specificScheduleDaysOfWeek = [
          changedValues?.specificScheduleDaysOfWeek?.[
            changedValues?.specificScheduleDaysOfWeek.length - 1
          ],
        ].filter(Boolean);
    }
    if (changedValues?.interval)
      updatedValues = {
        ...updatedValues,
        runEvery: 0,
        onDate: [],
        atDays: [],
        atMonth: [],
        specificScheduleDaysOfWeek: [],
        specificScheduleOrdinalNumeral: [],
      };
    if (changedValues?.atDays?.length > 0)
      updatedValues.specificScheduleDaysOfWeek = [];
    form.setFieldsValue(updatedValues);
  };
  const initScheduleDownload = (scheduleSetup = {}) => {
    const updateValues = {
      ...scheduleSetup,
      runAt: scheduleSetup?.runAt
        ? moment(scheduleSetup?.runAt)
        : moment('00:00:00', 'HH:mm:ss'),
      emails: scheduleSetup?.emails || userInfo?.email,
      fileType: scheduleSetup?.fileType?.toUpperCase() || 'XLSX',
      easterOrUserLocal: scheduleSetup?.easterOrUserLocal || 'America/Detroit',
      onDays: scheduleSetup?.onDays || ['1'],
    };
    form.setFieldsValue(updateValues);
  };
  const handleDeleteSchedule = async () => {
    await deleteSchedule.mutateAsync(
      { reportId: +reportingId },
      {
        onSuccess: (result) => {
          if (result?.isSuccess) {
            CustomNotification.success(
              'Delete reporting download schedule successfully'
            );
            queryClient.invalidateQueries({
              queryKey: getKeyFactPanels(reportingId),
            });
            closeModal();
          } else {
            CustomNotification.error(result?.message);
          }
        },
      }
    );
  };
  const closeModal = () => {
    form.resetFields();
    setOpenSchedule(false);
  };
  const handleValidateFTP = () => {
    validateFTP.mutate(
      {},
      {
        onSuccess: (result) => {
          if (result?.isSuccess) {
            setStatusFTP({ isValidated: true, isSuccess: true, message: '' });
          } else {
            setStatusFTP({
              isValidated: true,
              isSuccess: false,
              message: result?.message,
            });
          }
        },
      }
    );
  };
  useEffect(() => {
    if (openSchedule) initScheduleDownload(downloadScheduleDetail);
  }, [openSchedule, JSON.stringify(downloadScheduleDetail)]);
  const disableSubmitButton = !statusFTP?.isSuccess && deliveryMethod === 'ftp';
  const exportType =
    modeData === ADVANCED_FILTER_MODE.NESTED_DATA
      ? REPORT_EXPORT_TYPE_ADVANCED
      : REPORT_EXPORT_TYPE_FLAT;
  return (
    <StyledModal
      title='Reporting Download Schedule'
      visible={!!openSchedule}
      onCancel={closeModal}
      maskClosable={false}
      width={800}
      bodyStyle={{ height: 'min(600px, 60vh)' }}
      destroyOnClose
      footer={
        <ReportingScheduleFooter
          loading={configSchedule.isLoading}
          disableSubmitButton={disableSubmitButton}
          onSubmit={onSchedule}
          setOpenSchedule={setOpenSchedule}
          onDeleteScheduleButton={handleDeleteSchedule}
        />
      }
    >
      <Form
        form={form}
        slot='settings'
        id='reporting-schedule-form'
        className='scroller--y'
        initialValues={{ ...DEFAULT_SETTING }}
        style={{ height: '100%' }}
        onValuesChange={onFormChange}
      >
        <Row>
          <Col span={12}>
            <Form.Item
              labelCol={{ span: 10 }}
              wrapperCol={{ span: 14 }}
              name='deliveryMethod'
              label='Delivery Method'
              rules={[{ required: true, message: 'This field is required' }]}
            >
              <Radio.Group
                onChange={(e) => {
                  if (e.target.value === 'ftp' && !statusFTP?.isValidated) {
                    handleValidateFTP();
                  }
                }}
              >
                {DELIVERY_METHOD?.map((option, index) => {
                  const title = option?.toUpperCase();
                  return (
                    <Tooltip title={title} key={`method-${index}`}>
                      <Radio.Button
                        value={option.toLowerCase()}
                        style={{ padding: 4, height: 38, width: 32 }}
                      >
                        <TypePicker
                          style
                          group='download-method'
                          type={option}
                        />
                      </Radio.Button>
                    </Tooltip>
                  );
                })}
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col span={12}>
            {deliveryMethod === 'email' && (
              <Form.Item
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
                name='emails'
                label='Contact Email'
                tooltip={{
                  title: 'Add additional emails separated by commas.',
                  icon: <InfoCircleOutlined />,
                }}
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                  {
                    validator: (_, value) => {
                      const validateEmail = validateMultipleEmails(value);
                      if (validateEmail || !value) {
                        return Promise.resolve();
                      } else {
                        return Promise.reject('Invalid email format');
                      }
                    },
                  },
                ]}
              >
                <Input.TextArea />
              </Form.Item>
            )}
            {deliveryMethod === 'ftp' && (
              <Row>
                <Col span={24}>
                  <Button
                    loading={validateFTP.isLoading}
                    icon={<CloudSyncOutlined />}
                    type='primary'
                    onClick={handleValidateFTP}
                  >
                    Test Connection
                  </Button>
                </Col>
                {statusFTP?.isSuccess ? (
                  <Col span={24}>
                    <Button
                      loading={validateFTP.isLoading}
                      type='text'
                      style={{
                        color: '#52c41a',
                      }}
                    >
                      FTP Successfully Connected.
                    </Button>
                  </Col>
                ) : (
                  <Col span={24}>
                    <Button
                      loading={validateFTP.isLoading}
                      type='text'
                      style={{
                        color: '#f5222d',
                      }}
                    >
                      {statusFTP?.message}
                    </Button>
                  </Col>
                )}
              </Row>
            )}
          </Col>
        </Row>
        <Row>
          <Col offset={12} span={12}>
            <Form.Item
              labelCol={{ span: 8 }}
              wrapperCol={{ span: 16 }}
              name='fileType'
              label='Export Type'
              rules={[{ required: true, message: 'This field is required' }]}
            >
              <WrapperSelect
                allowClear={false}
                getPopupContainer={(triggerNode) => {
                  return triggerNode;
                }}
              >
                {[
                  exportType.map((type) => {
                    return (
                      <Select.Option value={type}>
                        {type?.toUpperCase()}
                      </Select.Option>
                    );
                  }),
                ]}
              </WrapperSelect>
            </Form.Item>
          </Col>
        </Row>
        <ReportingDownloadScheduleForm />
      </Form>
    </StyledModal>
  );
};
export default ReportingScheduleModal;
